'use client';

import { useEffect, useState } from 'react';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { ResolutionConfig, ScreenSize } from './types';

export const usePreloadFrameSequence = (
  path: string,
  screenSize: ScreenSize | null,
  resolutionMap: Record<ScreenSize, ResolutionConfig>,
  onLoad?: () => void,
) => {
  const [images, setImages] = useState<HTMLImageElement[]>([]);

  useEffect(() => {
    if (!screenSize) return;

    const frameCount = resolutionMap[screenSize].frameCount;
    const loadedImages: HTMLImageElement[] = [];

    const currentFrame = (index: number) =>
      getCompressedAssetUrl(
        `${path}${
          typeof window !== 'undefined' && window.innerWidth > 767
            ? resolutionMap.lg.dir_tag
            : resolutionMap.sm.dir_tag
        }/${(index + 1).toString().padStart(3, '0')}.png/${
          resolutionMap[screenSize].tag
        }`,
      );

    // 1. Show first image immediately for LCP
    const firstImg = new window.Image();
    (firstImg as any).priority = 'high';
    (firstImg as any).fetchPriority = 'high';
    firstImg.src = currentFrame(0);
    firstImg.onload = () => {
      if (onLoad) onLoad();
    };
    loadedImages[0] = firstImg;

    // 2. Load remaining frames with concurrency limit
    const CONCURRENCY = 8;
    let next = 1;
    let inFlight = 0;

    function loadFrame(idx: number) {
      return new Promise<void>((resolve) => {
        const img = new window.Image();
        img.loading = 'eager';
        (img as any).priority = 'high';
        (img as any).fetchPriority = 'high';
        img.src = currentFrame(idx);
        img.onload = img.onerror = () => {
          loadedImages[idx] = img;
          resolve();
        };
      });
    }

    async function loadAllFrames() {
      return new Promise<void>((resolveAll) => {
        function queueNext() {
          if (next >= frameCount && inFlight === 0) {
            resolveAll();
            return;
          }
          while (inFlight < CONCURRENCY && next < frameCount) {
            const idx = next++;
            inFlight++;
            loadFrame(idx).then(() => {
              inFlight--;
              queueNext();
            });
          }
        }
        queueNext();
      });
    }

    loadAllFrames().then(() => {
      setImages(loadedImages);
    });
  }, [screenSize, resolutionMap, onLoad]);

  return images;
};
